import React, { useCallback, useEffect, useRef } from 'react';

import { type EntryPoint, loadEntryPoint } from 'react-relay';

import Button from '@atlaskit/button/default/button';
import { cssMap } from '@atlaskit/css';
import { useId } from '@atlaskit/ds-lib/use-id';
import Heading from '@atlaskit/heading/heading';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Flex } from '@atlaskit/primitives/compiled/flex';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';
import { usePanelManager } from '@atlassian/panel-system';
import { PanelProvider } from '@atlassian/panel-system/panel-provider';
import { PanelRouter } from '@atlassian/panel-system/panel-router';
import { PanelsRenderer } from '@atlassian/panel-system/panels-renderer';
import { useRelayEnvironmentProvider } from '@atlassian/relay-environment-provider';

import { panelEntryPoint } from '../utils/dummy-entrypoint';
import DummyEntryPointComponent from '../utils/dummy-entrypoint-component';
import { RelayEnvironmentWrapper } from '../utils/relay-environment-wrapper';

const styles = cssMap({
	layout: {
		height: '100vh',
		width: '100%',
	},
	mainContent: {
		flex: 1,
		paddingBlockStart: token('space.300'),
		paddingInlineEnd: token('space.300'),
		paddingBlockEnd: token('space.300'),
		paddingInlineStart: token('space.300'),
		backgroundColor: token('color.background.neutral.subtle'),
	},
	panelWrapper: {
		borderInlineStart: `${token('border.width')} solid ${token('color.border')}`,
		width: '480px',
	},
	buttonGroup: {
		display: 'flex',
		gap: token('space.100'),
		flexWrap: 'wrap',
	},
	stateDisplay: {
		paddingBlockStart: token('space.200'),
		paddingInlineEnd: token('space.200'),
		paddingBlockEnd: token('space.200'),
		paddingInlineStart: token('space.200'),
		backgroundColor: token('color.background.neutral'),
	},
});

// Helper to get entry point name for display
function getEntryPointName(entryPoint: EntryPoint<any, any>): string {
	const root = entryPoint.root as any;
	return root?.getModuleName?.() || root?.getModuleId?.() || 'unknown-entrypoint';
}

function PanelManagerExample() {
	const { state, manager } = usePanelManager();
	const baseId = useId();
	const panelCounterRef = useRef(0);
	const environmentProvider = useRelayEnvironmentProvider();

	const openPageLoadPanel = useCallback(
		(reference: any) => {
			// Use replacePanelEntryPoint to replace any existing panels
			panelCounterRef.current += 1;
			const instanceId = `${baseId}-pageload-${panelCounterRef.current}`;
			manager.replacePanelEntryPoint(
				instanceId,
				panelEntryPoint,
				{ conversation: 'main' },
				reference,
			);
		},
		[manager, baseId],
	);

	const openNewPanel = useCallback(() => {
		panelCounterRef.current += 1;
		const instanceId = `${baseId}-new-${panelCounterRef.current}`;
		manager.replacePanel(instanceId, <DummyEntryPointComponent />, { pageId: 'page-789' });
	}, [manager, baseId]);

	// Open a panel on mount for demonstration
	useEffect(() => {
		// Mimic the behaviour of loading an entrypoint on page load
		const entryPointReference = loadEntryPoint<typeof panelEntryPoint>(
			environmentProvider,
			panelEntryPoint,
			{},
		);
		if (entryPointReference && state.activePanels.length === 0) {
			openPageLoadPanel(entryPointReference);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [environmentProvider]);

	return (
		<Flex xcss={styles.layout}>
			<Box xcss={styles.mainContent}>
				<Stack space="space.300">
					<Heading size="medium">Panel Manager + PanelsRenderer Example</Heading>
					<Text>
						This example demonstrates using both the panel manager for programmatic control and
						PanelsRenderer for automatic rendering. Use the manager to open/close panels and read
						state, while PanelsRenderer handles rendering automatically. The `replacePanel` method
						is used to ensure only one panel is open at a time (single panel mode).
					</Text>

					<Text>An EntryPoint panel opens by default on page load.</Text>

					<Stack space="space.200">
						<Heading size="small" as="h3">
							Actions
						</Heading>
						<Box xcss={styles.buttonGroup}>
							<Button onClick={openNewPanel} testId="open-new-button">
								Open New Panel
							</Button>
						</Box>
					</Stack>

					<Stack space="space.200">
						<Heading size="small" as="h3">
							Active Panels: {state.activePanels.length}
						</Heading>
						{state.activePanels.length === 0 ? (
							<Text color="color.text.subtle">No active panels</Text>
						) : (
							<Box xcss={styles.stateDisplay}>
								<pre>
									{JSON.stringify(
										state.activePanels.map((panel) => ({
											instanceId: panel.instanceId,
											entryPoint:
												'entryPoint' in panel && panel.entryPoint
													? getEntryPointName(panel.entryPoint)
													: 'N/A',
											params: panel.params,
										})),
										null,
										2,
									)}
								</pre>
							</Box>
						)}
					</Stack>
				</Stack>
			</Box>

			<PanelsRenderer>
				<PanelRouter
					wrapper={({ children }: { children: React.ReactNode }) => (
						<Box xcss={styles.panelWrapper}>{children}</Box>
					)}
					errorFallback={() => <Text as="p">Error loading panel</Text>}
				/>
			</PanelsRenderer>
		</Flex>
	);
}

export default function PanelManagerExampleWithProvider(): React.JSX.Element {
	return (
		<PanelProvider>
			<RelayEnvironmentWrapper>
				<PanelManagerExample />
			</RelayEnvironmentWrapper>
		</PanelProvider>
	);
}
